/**
 * Idle timeout.
 *
 * After a stretch with no activity the person is warned and given a countdown to
 * say they're still there. Letting the countdown run out signs them out.
 *
 *		import { useIdleTimeout } from "/helpers/useIdleTimeout.js";
 *
 *		const idle = useIdleTimeout({ onTimeout: () => signOut(SIGN_OUT_REASON.Idle) });
 *
 *		idle.isWarning			true while the warning is on screen
 *		idle.countdownDisplay	"1:59" - already formatted, ready to render
 *		idle.staySignedIn()		dismiss the warning and start the clock again
 *
 * While the warning is showing, activity is deliberately ignored: a stray mouse
 * move must not silently keep a session alive on an unattended screen. The
 * person makes an explicit choice.
 *
 * Scope: one browser tab. Signing out in one tab does not close the countdown
 * running in another - each tab discovers the dead session on its next API call.
 */

import { ref, computed, onMounted, onBeforeUnmount } from "vue";


/**
 * How long the portal waits, and how long the warning lasts. Both in one place
 * so the timing is a config change rather than a hunt through the composable.
 */
export const IDLE_TIMEOUT_CONFIG = {
	// Minutes of no activity before the warning appears.
	idleMinutes: 20,

	// Seconds the warning counts down before signing the person out.
	warningSeconds: 120,
};


// The events that count as "still there". Passive listeners - none of these
// handlers do anything a browser needs to wait for.
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "wheel", "touchstart", "scroll"];




//-- Methods

/**
 * A countdown as m:ss, for display. Formatting lives here rather than in the
 * template that shows it.
 *
 * @param {number} totalSeconds Seconds remaining.
 * @returns {string} e.g. "1:59".
 */
export function formatCountdown(totalSeconds)
{
	const safeSeconds = totalSeconds > 0 ? Math.floor(totalSeconds) : 0;

	const minutes = Math.floor(safeSeconds / 60);
	const seconds = safeSeconds % 60;

	return minutes + ":" + String(seconds).padStart(2, "0");
}



/**
 * Watch for inactivity and drive the warning countdown.
 *
 * @param {function} options.onTimeout Called once when the countdown reaches zero.
 * @param {number} options.idleMinutes Override the configured idle window.
 * @param {number} options.warningSeconds Override the configured countdown length.
 * @param {boolean} options.enabled Set false to stand the whole thing down (e.g. on a public page).
 */
export function useIdleTimeout(options = {})
{
	const onTimeout = options.onTimeout ? options.onTimeout : () => { };
	const idleMinutes = options.idleMinutes ? options.idleMinutes : IDLE_TIMEOUT_CONFIG.idleMinutes;
	const warningSeconds = options.warningSeconds ? options.warningSeconds : IDLE_TIMEOUT_CONFIG.warningSeconds;
	const enabled = options.enabled === false ? false : true;

	const isWarning = ref(false);
	const secondsRemaining = ref(warningSeconds);

	const countdownDisplay = computed(() => formatCountdown(secondsRemaining.value));

	let lastActivityAt = Date.now();
	let tickTimer = null;
	let hasTimedOut = false;


	const idleMilliseconds = idleMinutes * 60 * 1000;


	const onActivity = () =>
	{
		// An unattended screen must not be able to keep itself signed in, so
		// activity counts for nothing once the warning is up.
		if (isWarning.value) return;

		lastActivityAt = Date.now();
	};


	const tick = () =>
	{
		if (isWarning.value)
		{
			secondsRemaining.value = secondsRemaining.value - 1;

			if (secondsRemaining.value > 0) return;

			// Time's up. Only ever fires once.
			if (hasTimedOut) return;
			hasTimedOut = true;

			stop();
			onTimeout();

			return;
		}

		if (Date.now() - lastActivityAt < idleMilliseconds) return;

		// Gone quiet for long enough - start the countdown.
		secondsRemaining.value = warningSeconds;
		isWarning.value = true;
	};


	const start = () =>
	{
		if (tickTimer != null) return;

		tickTimer = setInterval(tick, 1000);
		ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, onActivity, { passive: true }));
	};


	const stop = () =>
	{
		if (tickTimer != null)
		{
			clearInterval(tickTimer);
			tickTimer = null;
		}

		ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, onActivity));
	};


	/**
	 * The person said they're still here. Clear the warning and start over.
	 */
	const staySignedIn = () =>
	{
		isWarning.value = false;
		secondsRemaining.value = warningSeconds;
		lastActivityAt = Date.now();
	};




	//-- Lifecycle Hooks

	onMounted(() =>
	{
		if (enabled == false) return;

		lastActivityAt = Date.now();
		start();
	});


	onBeforeUnmount(() =>
	{
		stop();
	});


	return { isWarning, secondsRemaining, countdownDisplay, staySignedIn, stop };
}


export default { useIdleTimeout, formatCountdown, IDLE_TIMEOUT_CONFIG };
